// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { defineComponent } from '@shared/ui-kit/utils/define-component';
import { CashflowChart } from './cashflow-chart';
import { CashflowTable } from './cashflow-table';
import {
  KpiBoxes,
  SectionContainer,
  PeriodPicker,
  Portal,
} from '@shared/ui-kit/components';
import { Loader } from '../loader';
import { useCashflow } from './use-cashflow';
import { RefreshControl } from '../refresh-control';
import { AccountsSelector } from '../accounts-selector';

export const Cashflow = defineComponent({
  displayName: 'Cashflow',
  useSetup: useCashflow,
  View: ({
    t,
    status,
    kpis,
    transactions,
    hasTransactions,
    onRefresh,
    updatedDate,
    refreshMode,
    accountOptions,
    onChangeAccounts,
    selectedAccounts,
    periodPicker,
    cashflowTableData,
    institutionId,
    isSyncing,
    tags,
    tagsByObject,
    selectedTag,
    setSelectedTag,
  }) => {
    return (
      <>
        <Portal container="aggregation-institutions-actions">
          <AccountsSelector
            data={accountOptions}
            onChange={onChangeAccounts}
            selectedAccounts={selectedAccounts}
            tags={tags}
            selectedTag={selectedTag}
            onTagSelect={setSelectedTag}
            tagsByObject={tagsByObject}
          />
        </Portal>

        <div className="flex flex-col gap-y-6" data-testid="cashflow-container">
          <div className="flex w-full flex-col items-stretch gap-y-3 md:flex-row md:items-center md:justify-between">
            <RefreshControl
              updatedDate={updatedDate}
              onRefresh={onRefresh}
              disabled={status === 'pending' || isSyncing}
              mode={refreshMode}
            />

            <div className="flex shrink-0 justify-end">
              <PeriodPicker
                onValueChange={periodPicker.onPeriodChange}
                defaultValue={periodPicker.defaultHist}
                availableOptions={['1M', '3M', '6M', '1Y']}
              />
            </div>
          </div>

          <Loader
            status={status}
            shouldDisplayContent={hasTransactions}
            shouldDisplayEmptyState={!hasTransactions}
            institution={institutionId}
            onRefresh={onRefresh}
          >
            {hasTransactions && (
              <div className="mb-4 grid grid-cols-2 gap-6 md:grid-cols-4">
                {kpis.map((kpi, idx) => (
                  <KpiBoxes key={idx} {...kpi} />
                ))}
              </div>
            )}

            {hasTransactions && (
              <>
                <SectionContainer
                  title={t('glossary.cashflow', { ns: 'glossary' })}
                >
                  <CashflowChart data={transactions} />
                </SectionContainer>

                <SectionContainer>
                  <CashflowTable
                    data={cashflowTableData.data as any}
                    months={cashflowTableData.months}
                  />
                </SectionContainer>
              </>
            )}
          </Loader>
        </div>
      </>
    );
  },
});
